import {
    type CreateExtArgs,
    type CreateExtensionArgument,
    type DAL,
    type DalJsStore,
    type DalValue,
    type DeepFunctionMap,
    type DmApis,
    type Extension,
    type ExtensionAPI,
    PAGE_FRAGMENT_TYPE,
    pointerUtils,
    type PostTransactionOperation,
    type SnapshotDal,
    type ValidatorMap
} from '@wix/document-manager-core'
import type {
    BuilderStyle,
    CompRef,
    ConversionAlgoConfigOverrides,
    MobileAlgoExecutionOptions,
    Pointer,
    Pointers,
    ResolvedFunctions,
    Transaction,
    VariantPointer,
    AutoResponsivenessMode,
    GridItemLayout
} from '@wix/document-services-types'
import {
    MobileAlgoConditionType,
    AutoResponsivenessMode as AutoResponsivenessModeValue
} from '../../constants/dsTypesConstants'
import type {
    AppliedHeuristicLogMessage,
    BaseStageData,
    ConversionAlgoConfig,
    ConversionDal,
    FallbackMeasurementsLoggingContext,
    InnerElementStageHandler,
    MissingMeasurementInfo,
    MobileAlgoContext,
    MobileAlgoContextInternalStore,
    MobileAlgoPluginFactory,
    MobileAlgoPluginInitializationArgs,
    MobileAlgoPluginWithContext,
    MobileAlgoRunFunction,
    PluginStageHandler,
    PostStageHandler,
    PrepareConversionDataResult,
    ReadOnlyExtensionAPI,
    Stage,
    Stages,
    StructureIterators
} from './types'
import {ChildrenProperties, NaryTreeTraversalOrder, StageName, LockingAction} from './enums'
import {
    RESPONSIFY_ALGO_BATCH_INTERACTION_NAME,
    RESPONSIFY_ALGO_BATCH_RUN_ID_PREFIX,
    RESPONSIFY_ALGO_BASELINE_TRANSACTION_NAME,
    RESPONSIFY_ALGO_FALLBACK_MEASUREMENTS_INTERACTION_NAME,
    RESPONSIFY_ALGO_RUN_INTERACTION_NAME,
    RESPONSIFY_ALGO_RUN_ID_PREFIX,
    RESPONSIFY_ALGO_TRANSACTION_NAME
} from './constants'
import _ from 'lodash'
import {plugins} from './plugins'
import {createStructureStage} from './stages/structureStage'
import {createComponentStage} from './stages/componentStage'
import {createPluginContextHelper} from './pluginLifeCycle/pluginContextHelper'
import {createPluginHeuristicsRegistry} from './pluginLifeCycle/pluginHeuristicsRegistry'
import {createPluginContext} from './pluginLifeCycle/pluginContext'
import {createConversionDal} from './conversionDal/conversionDal'
import {createReadOnlyExtensionApi} from './readOnlyExtensionApi'
import type {VariantsExtensionAPI} from '../variants/variants'
import type {RelationshipsAPI} from '../relationships'
import type {GroupApi} from './plugins/grouping/types'
import {sendRequest} from './plugins/grouping/request'
import {DATA_TYPES, VARIANTS, VIEW_MODES, COMP_TYPES, MASTER_PAGE_ID} from '../../constants/constants'
import {conversionDataBuilders} from './preprocess'
import {mobileConversionValidation} from './validation'
import {createStructureIterators} from './conversionDal/utils/structureIterators'
import {ConversionDalNamespaces} from './conversionDal/constants'
import {
    getInnerElementPointer,
    getStructurePointer,
    getStylePointer,
    getTargetLayoutPointer,
    getTransformationsPointer
} from './conversionDal/utils/pointer'
import type {PageExtensionAPI} from '../page'
import {getMobileBreakpoint} from './preprocess/utils/mobileBreakPoint'
import type {MeasurementsExtensionAPI} from '../measurements/measurements'
import type {LayoutExtensionApi} from '../layout/layout'
import type {RMApi} from '../rendererModel'
import type {InnerElementsExtensionAPI} from '../builderComponents/innerElements'
import {StageRunner} from './stages/stageRunner'
import {
    getMobileFontSize as getFontSize,
    getReportableFromError,
    ReportableError,
    ReportableWarning
} from '@wix/document-manager-utils'
import {deepClone} from '@wix/wix-immutable-proxy'
import type {BuilderStyleExtensionAPI} from '../builderComponents/builderStyle'
import type {BuilderComponentsExtensionAPI} from '../builderComponents/builderComponents'
import type {StructureExtensionAPI} from '../structure'
import * as conversionDataTransformers from './postprocess/conversionDataTransformers'
import type {OrderApi} from './plugins/order/types'
import type {LayoutSettingsExtensionAPI} from '../layoutSettings/types'
import type {SlotsExtensionAPI} from '../slots/slots'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'

export const DOCUMENT_STAGE_WIDTH = 1280
export const MOBILE_VARIANT = {type: 'variants', id: VARIANTS.MOBILE_VARIANT_ID}

const getTargetBreakpointForComponent = (dal: DAL, pointers: Pointers, compRef: CompRef): VariantPointer => {
    const pageMobileBreakpoint = getMobileBreakpoint(
        dal,
        pointers,
        pointers.structure.getPageOfComponent(compRef) as CompRef
    )!
    const componentBreakpoint = getMobileBreakpoint(dal, pointers, compRef)
    const masterPageBreakpoint = getMobileBreakpoint(
        dal,
        pointers,
        pointers.structure.getMasterPage(VIEW_MODES.DESKTOP) as CompRef
    )

    return componentBreakpoint ?? pageMobileBreakpoint ?? masterPageBreakpoint ?? MOBILE_VARIANT
}

const SECTION_COMP_TYPES = new Set([
    COMP_TYPES.FOOTER_SECTION,
    COMP_TYPES.RESPONSIVE_SECTION,
    COMP_TYPES.HEADER_SECTION
])
export interface MobileAlgoApi extends ExtensionAPI {
    groupings: {
        removeLastGroupingHashFromAllPages(): void
        removeLastGroupingHashFromPage(pagePointer: CompRef): void
        removeLastGroupingHashFromSection(sectionPointer: Pointer): void
        getLastGroupingHash(compRef: CompRef): string | null
    }
    locking: {
        isComponentLocked(compRef: CompRef): boolean
        unlockSection(sectionPointer: CompRef): void
        unlockSectionsForPage(pagePointer: CompRef): void
        unlockSectionsForAllPages(): void
        lockSection(sectionPointer: CompRef): void
        lockSectionsForPage(pagePointer: CompRef): void
        lockSectionsForAllPages(): void
        softLock(compRef: CompRef): Promise<void>
        softLockPage(pageRef: CompRef): Promise<void[]>
        softLockAllPages(): Promise<void[]>
        softUnlock(sectionPointer: CompRef): void
        softUnlockSectionsForPage(pagePointer: CompRef): void
        softUnlockSectionsForAllPages(): void
        removeExecutionLocks(sectionPointer: CompRef): void
        removeExecutionLocksForPage(pagePointer: CompRef): void
        removeExecutionLocksForAllPages(): void
    }
    context: {
        enable(ctx: MobileAlgoContext, plugins?: string[]): void
        disable(ctx: MobileAlgoContext, plugins?: string[]): void
        create(enabledPlugins?: string[]): MobileAlgoContext
    }
    plugins: {
        register(pluginFactory: MobileAlgoPluginFactory): void
        getApis(ctx: MobileAlgoContext): DeepFunctionMap
    }
    utils: {
        getFontSize(fontCssString: string): {
            targetFontSize?: number
            unit?: string
        }
    }
    heuristics: HeuristicRegistry
    createConversionConfig(
        containerPointer: CompRef,
        configurationOverrides?: ConversionAlgoConfigOverrides
    ): ConversionAlgoConfig
    writeConversionResult(
        conversionDal: ConversionDal,
        initialSnapshot: SnapshotDal,
        structureIterators: StructureIterators,
        conversionAlgoConfig: ConversionAlgoConfig,
        ctx: MobileAlgoContext
    ): {changeApplied: boolean}
    runWithContext(
        containerPointer: CompRef,
        ctx: MobileAlgoContext,
        config: ConversionAlgoConfig,
        algorithmRunId?: string
    ): Promise<{
        conversionDal: ConversionDal
        initialSnapshot: SnapshotDal
        structureIterators: StructureIterators
    }>
    run(
        containerPointer: CompRef,
        config?: ConversionAlgoConfigOverrides,
        executionOptions?: MobileAlgoExecutionOptions
    ): Promise<void>
    runForAllPages(executionOptions?: MobileAlgoExecutionOptions): Promise<void>
    runForPage(
        pagePointer: CompRef,
        configOverrides?: ConversionAlgoConfigOverrides,
        executionOptions?: MobileAlgoExecutionOptions
    ): Promise<void>
    runForPages(
        pageIds: string[],
        configOverrides?: ConversionAlgoConfigOverrides,
        executionOptions?: MobileAlgoExecutionOptions
    ): Promise<void>
    settings: {
        enable(): void
        disable(): void
        isEnabled(): boolean
    }
    state: {
        isRunning(): boolean
    }
}

export interface HeuristicRegistry extends ExtensionAPI {
    registerHeuristic<T extends BaseStageData>(stage: Stage<T>, handler: PluginStageHandler<T>): void
    registerInnerElementHeuristic<T extends BaseStageData>(stage: Stage<T>, handler: InnerElementStageHandler): void
    registerPostStageOperation<T extends BaseStageData>(stage: Stage<T>, handler: PostStageHandler): void
}

export interface MobileAlgoExtensionApi extends ExtensionAPI {
    mobileAlgo: MobileAlgoApi
}

const createExtension = ({environmentContext, experimentInstance, logger}: CreateExtensionArgument) => {
    const bootPlugins: MobileAlgoPluginFactory[] = Object.values(plugins)
    let executingMobileAlgo = false

    const createExtensionAPI = ({pointers, dal, extensionAPI}: CreateExtArgs): MobileAlgoExtensionApi => {
        const variantsApi = () => (extensionAPI as VariantsExtensionAPI).variants
        const layoutSettingsApi = () => (extensionAPI as LayoutSettingsExtensionAPI).layoutSettings
        const dataModelApi = () => (extensionAPI as DataModelExtensionAPI).dataModel
        const pages = () => (extensionAPI as PageExtensionAPI).page
        const readOnlyExtensionAPI: ReadOnlyExtensionAPI = createReadOnlyExtensionApi(extensionAPI)

        const masterPagePointer = pointerUtils.getPointer(MASTER_PAGE_ID, VIEW_MODES.DESKTOP) as CompRef

        const getResponsifyAlgoSettings = () => {
            const editorSettings = dataModelApi().components.getItem(masterPagePointer, DATA_TYPES.editorsettings)
            return editorSettings?.responsifyAlgo
        }

        const updateResponsifyAlgoSettings = (disabled: boolean) => {
            const editorSettings = dataModelApi().components.getItem(masterPagePointer, DATA_TYPES.editorsettings) ?? {}
            dataModelApi().components.addItem(masterPagePointer, DATA_TYPES.editorsettings, {
                ...editorSettings,
                type: 'EditorSettings',
                responsifyAlgo: {disabled}
            })
        }

        const isEnabled = (): boolean => !getResponsifyAlgoSettings()?.disabled

        const enable = (): void => updateResponsifyAlgoSettings(false)

        const disable = (): void => updateResponsifyAlgoSettings(true)
        const stages: Stages = {
            [StageName.COMPONENT_CATEGORIZATION]: createComponentStage(),
            [StageName.GROUPING]: createStructureStage(),
            [StageName.GROUPING_ORDER]: createComponentStage({
                structuralType: ConversionDalNamespaces.virtualStructure
            }),
            [StageName.INIT_CSS_MODELS]: createComponentStage(),
            [StageName.POSITION]: createComponentStage(),
            [StageName.STRUCTURE_ORDER]: createComponentStage(),
            [StageName.HORIZONTAL_LAYOUT_SCALE]: createComponentStage(),
            [StageName.VERTICAL_LAYOUT_SCALE]: createComponentStage({
                traversalOrder: NaryTreeTraversalOrder.PostOrder
            }),
            [StageName.TRANSFORM_STYLE]: createComponentStage(),
            [StageName.POST_PROCESS]: createComponentStage()
        }
        const actionsLogger: AppliedHeuristicLogMessage[] = []
        const pluginRegistry = new Map<string, MobileAlgoPluginWithContext>()

        const setMobileAlgoExecutionState = (isRunning: boolean) => (executingMobileAlgo = isRunning)

        const getMobileAlgoExecutionState = () => executingMobileAlgo

        const getPageId = (containerPointer: CompRef) => pointers.structure.getPageOfComponent(containerPointer).id

        const filterPlugins = (pluginsToFilter?: string[]): MobileAlgoPluginWithContext[] => {
            if (!pluginsToFilter) {
                return Array.from(pluginRegistry.values())
            }

            return Array.from(pluginRegistry.values()).filter(plugin => pluginsToFilter.includes(plugin.name))
        }

        const disablePluginsForContext = (ctx: MobileAlgoContext, pluginsToDisable?: string[]): void => {
            filterPlugins(pluginsToDisable).forEach(plugin => {
                plugin.contextHelper.disable(ctx)
            })
        }

        const enablePluginsForContext = (ctx: MobileAlgoContext, pluginsToEnable?: string[]) => {
            filterPlugins(pluginsToEnable).forEach(plugin => {
                if (plugin.contextHelper.isEnabled(ctx)) {
                    return
                }

                plugin.contextHelper.enable(ctx)

                if (plugin.dependencies) {
                    enablePluginsForContext(ctx, plugin.dependencies as any)
                }
            })
        }

        const getPluginApis = (ctx: MobileAlgoContext): DeepFunctionMap => {
            const result: DeepFunctionMap = {}

            Array.from(pluginRegistry.values()).forEach((plugin: MobileAlgoPluginWithContext) => {
                if (plugin.createApi) {
                    const pluginContext = createPluginContext(plugin.contextHelper, ctx)
                    result[plugin.name] = plugin.createApi(pluginContext)
                }
            })

            return result
        }

        const registerRequest = (ctx: MobileAlgoContext) => {
            const apis = getPluginApis(ctx)
            const {grouping} = apis as GroupApi
            grouping.registerServer(sendRequest)
        }

        const createContextStore = () => {
            const internalStore: MobileAlgoContextInternalStore = {}
            return {
                get: (key: string) => {
                    return internalStore[key]
                },
                set: (key: string, value: any) => {
                    internalStore[key] = value
                }
            }
        }

        const createContext = (enabledPlugins?: string[]): MobileAlgoContext => {
            const ctx: MobileAlgoContext = createContextStore()

            if (enabledPlugins) {
                disablePluginsForContext(ctx)
                enablePluginsForContext(ctx, enabledPlugins)
            } else {
                enablePluginsForContext(ctx)
            }

            registerRequest(ctx)

            return ctx
        }

        const registerPlugin = (pluginFactory: MobileAlgoPluginFactory) => {
            const algoApi = (extensionAPI as MobileAlgoExtensionApi).mobileAlgo
            const initializeArgs: MobileAlgoPluginInitializationArgs = {
                experimentInstance,
                readOnlyExtensionAPI,
                stages,
                environmentContext,
                logger
            }
            const plugin = pluginFactory.createPlugin(initializeArgs)
            const contextHelper = createPluginContextHelper(plugin.name)
            const pluginWithContext = {...plugin, contextHelper}
            const heuristicsRegistry = createPluginHeuristicsRegistry(algoApi.heuristics, contextHelper)
            pluginRegistry.set(plugin.name, pluginWithContext)
            plugin.register(heuristicsRegistry)
        }

        const registerHeuristic = <T extends BaseStageData>(stage: Stage<T>, handler: PluginStageHandler<T>) => {
            stage.register(handler)
        }

        const registerInnerElementHeuristic = <T extends BaseStageData>(
            stage: Stage<T>,
            handler: InnerElementStageHandler
        ) => {
            stage.registerInnerElementHandler?.(handler)
        }

        const registerPostStageOperation = <T extends BaseStageData>(stage: Stage<T>, handler: PostStageHandler) => {
            stage.registerPostStageOperation?.(handler)
        }

        interface IdAndDepth {
            id: string
            depth: number
        }

        const setFallbackMeasurements = (
            containerPointer: CompRef,
            initialStore: DalJsStore,
            loggingContext: FallbackMeasurementsLoggingContext
        ) => {
            const fallBackMeasurements = (extensionAPI as MeasurementsExtensionAPI).measurements.generateMeasurement(
                containerPointer
            )
            const queue: IdAndDepth[] = [{id: containerPointer.id, depth: 0}]

            logger.interactionStarted(RESPONSIFY_ALGO_FALLBACK_MEASUREMENTS_INTERACTION_NAME, {
                extras: {
                    algorithmRunId: loggingContext.algorithmRunId,
                    rootComponentId: loggingContext.rootComponentId,
                    componentsCount: loggingContext.componentsMissingMeasurements.length,
                    components: loggingContext.componentsMissingMeasurements
                }
            })

            while (queue.length > 0) {
                const {id, depth} = queue.shift()!
                const compPointer = pointerUtils.getPointerFromPointer(id, containerPointer) as CompRef
                const component = dal.get(compPointer)

                initialStore[ConversionDalNamespaces.measurements][id] = fallBackMeasurements[id]
                initialStore[ConversionDalNamespaces.structure][id] = {
                    ...initialStore[ConversionDalNamespaces.structure][id],
                    axisAlignedEnvelope: conversionDataBuilders.createEnvelope(fallBackMeasurements[id], 0)
                }

                for (const childId of component.components ?? []) {
                    queue.push({id: childId, depth: depth + 1})
                }
            }

            logger.interactionEnded(RESPONSIFY_ALGO_FALLBACK_MEASUREMENTS_INTERACTION_NAME, {
                extras: {algorithmRunId: loggingContext.algorithmRunId}
            })
        }

        const prepareConversionData = (
            containerPointer: CompRef,
            conversionAlgoConfig: ConversionAlgoConfig,
            algorithmRunId?: string
        ): PrepareConversionDataResult => {
            let useFallBack = false
            const componentsMissingMeasurements: MissingMeasurementInfo[] = []
            const queue: IdAndDepth[] = [{id: containerPointer.id, depth: 0}]
            const initialStore: DalJsStore = {
                [ConversionDalNamespaces.structure]: {},
                [ConversionDalNamespaces.measurements]: {},
                [ConversionDalNamespaces.sourceLayout]: {},
                [ConversionDalNamespaces.targetLayout]: {},
                [ConversionDalNamespaces.style]: {},
                [ConversionDalNamespaces.transformations]: {},
                [ConversionDalNamespaces.innerElements]: {},
                [ConversionDalNamespaces.resolvedStyle]: {}
            }
            const shouldValidateComponentLayout = experimentInstance.isOpen(
                'dm_shouldValidateComponentLayoutMobileAlgo'
            )
            const {components, slots} = extensionAPI as StructureExtensionAPI & SlotsExtensionAPI

            while (queue.length > 0) {
                const {id, depth} = queue.shift()!
                const compPointer = pointerUtils.getPointerFromPointer(id, containerPointer) as CompRef
                const component = dal.get(compPointer)
                const parentComponent = dal.get({id: component.parent, type: VIEW_MODES.DESKTOP})

                if (shouldValidateComponentLayout) {
                    mobileConversionValidation(component, parentComponent, containerPointer)
                }

                const conversionData = conversionDataBuilders.getConversionData(
                    dal,
                    extensionAPI,
                    pointers,
                    compPointer,
                    conversionAlgoConfig
                )

                const targetLayout = conversionData[ConversionDalNamespaces.targetLayout]?.[id]

                if (
                    targetLayout &&
                    id !== containerPointer.id &&
                    !initialStore[ConversionDalNamespaces.structure][parentComponent.id]?.isSlottedComponent
                ) {
                    const {hidden} = targetLayout.componentLayout ?? {}
                    const {itemLayout} = targetLayout

                    if (
                        (_.isEmpty(itemLayout) ||
                            ((itemLayout as GridItemLayout)?.type === 'GridItemLayout' &&
                                _.isEmpty((itemLayout as GridItemLayout)?.gridArea))) &&
                        !hidden
                    ) {
                        delete conversionData[ConversionDalNamespaces.targetLayout]![id]
                        logger.captureError(
                            new ReportableWarning({
                                errorType: 'InvalidTargetLayoutInAdaptiveMode',
                                message: `Stripped corrupted targetLayout: itemLayout is empty`,
                                extras: {compId: id, rootCompId: containerPointer.id, targetLayout}
                            })
                        )
                    }
                }

                for (const [namespace, value] of Object.entries(conversionData)) {
                    _.merge(initialStore[namespace], value)
                }

                const isMeasurementsEmpty = _.isEmpty(initialStore[ConversionDalNamespaces.measurements][id])

                const isComponentHidden =
                    initialStore[ConversionDalNamespaces.sourceLayout][id]?.componentLayout?.hidden

                if (isMeasurementsEmpty && !isComponentHidden && !slots.isChildOfSlottedComp(id)) {
                    useFallBack = true
                    componentsMissingMeasurements.push({
                        compId: id,
                        componentType: components.getComponentType(compPointer)
                    })
                }

                for (const childId of component.components ?? []) {
                    queue.push({id: childId, depth: depth + 1})
                }
            }

            if (useFallBack) {
                setFallbackMeasurements(containerPointer, initialStore, {
                    algorithmRunId,
                    rootComponentId: containerPointer.id,
                    componentsMissingMeasurements
                })
            }

            return {initialStore, componentsMissingMeasurements, usedFallbackMeasurements: useFallBack}
        }

        const getCompPointerWithVariants = (compPointer: CompRef, targetVariants?: VariantPointer[]) => {
            return targetVariants
                ? pointers.components.getComponentVariantsPointer(compPointer, targetVariants)
                : compPointer
        }

        const updateLayoutSettings = (conversionAlgoConfig: ConversionAlgoConfig, ctx: MobileAlgoContext) => {
            const apis = getPluginApis(ctx)
            const {order} = apis as OrderApi
            const groupingHashResult = order.getCalculatedHash()

            if (groupingHashResult && groupingHashResult !== conversionAlgoConfig.lastGroupingHash) {
                layoutSettingsApi().autoResponsivenessSettings.lastGroupingsHash.set(
                    conversionAlgoConfig.rootPointer,
                    groupingHashResult
                )
            }

            layoutSettingsApi().autoResponsivenessSettings.isSynced.set(
                getCompPointerWithVariants(conversionAlgoConfig.rootPointer, conversionAlgoConfig.targetVariants),
                true
            )
        }

        const writeConversionResult = (
            conversionDal: ConversionDal,
            initialSnapshot: SnapshotDal,
            structureIterators: StructureIterators,
            conversionAlgoConfig: ConversionAlgoConfig,
            ctx: MobileAlgoContext
        ): {changeApplied: boolean} => {
            const {builderComponents} = extensionAPI as BuilderComponentsExtensionAPI
            const {components} = extensionAPI as StructureExtensionAPI
            const {builderStyle} = extensionAPI as BuilderStyleExtensionAPI

            const updateResponsiveLayout = (compPointer: CompRef, changes: DalJsStore) => {
                if (!changes[ConversionDalNamespaces.targetLayout]?.[compPointer.id]) {
                    return
                }

                const layoutToUpdate = deepClone(conversionDal.get(getTargetLayoutPointer(compPointer)))
                const layoutTransformer = conversionDataTransformers.getLayoutTransformer(
                    extensionAPI,
                    compPointer,
                    conversionAlgoConfig
                )
                const transformedLayout = layoutTransformer(layoutToUpdate)

                variantsApi().updateComponentDataConsideringVariants(compPointer, transformedLayout, DATA_TYPES.layout)
            }

            const updateStyle = (compPointer: CompRef, changes: DalJsStore) => {
                if (!changes[ConversionDalNamespaces.style]?.[compPointer.id]) {
                    return
                }

                const styleTransformer = conversionDataTransformers.getStyleTransformer(
                    extensionAPI,
                    compPointer,
                    deepClone(conversionDal.get(getStylePointer(compPointer)))
                )

                const styleItem = styleTransformer()

                if (!styleItem) {
                    return
                }

                if (!builderComponents.isBuilder(components.getComponentType(compPointer))) {
                    return variantsApi().updateComponentDataConsideringVariants(
                        compPointer,
                        styleItem,
                        DATA_TYPES.theme
                    )
                }

                return builderStyle.component.updateCSSStyle(compPointer, styleItem)
            }

            const updateInnerElements = (compPointer: CompRef, changes: DalJsStore) => {
                if (!changes[ConversionDalNamespaces.style]) {
                    return
                }

                const innerElementRootPointer = getInnerElementPointer({id: compPointer.id})

                if (!conversionDal.get(innerElementRootPointer)) {
                    return
                }

                const innerElementIterators = createStructureIterators(conversionDal, innerElementRootPointer)

                innerElementIterators.forEachComponent(
                    (innerElementPointer: Pointer) => {
                        if (innerElementPointer.id === compPointer.id) {
                            return
                        }

                        if (!changes[ConversionDalNamespaces.style]?.[innerElementPointer.id]) {
                            return
                        }

                        const innerElementStyleItem = deepClone(conversionDal.get(getStylePointer(innerElementPointer)))

                        if (!innerElementStyleItem) {
                            return
                        }

                        const styleTransformer = conversionDataTransformers.getStyleTransformer(
                            extensionAPI,
                            compPointer,
                            innerElementStyleItem
                        )

                        const transformedStyleItem = styleTransformer()

                        if (!transformedStyleItem) {
                            return
                        }

                        const {innerElements} = extensionAPI as InnerElementsExtensionAPI
                        const innerElementPath = innerElementPointer.id.split('.').slice(1)

                        innerElements.setInnerElementStyle(
                            compPointer,
                            innerElementPath,
                            transformedStyleItem as Partial<BuilderStyle>
                        )
                    },
                    [ChildrenProperties.innerElements]
                )
            }

            const updateTransformations = (compPointer: CompRef, changes: DalJsStore) => {
                if (!changes[ConversionDalNamespaces.transformations]?.[compPointer.id]) {
                    return
                }

                const transformationsItem = deepClone(conversionDal.get(getTransformationsPointer(compPointer)))
                const {variants} = extensionAPI as VariantsExtensionAPI
                let baseTransformationsItem = variants.getComponentItemConsideringVariants(
                    compPointer,
                    DATA_TYPES.transformations
                )
                if (!baseTransformationsItem) {
                    baseTransformationsItem = dal.schema.createItemAccordingToSchema(
                        'TransformData',
                        DATA_TYPES.transformations
                    )
                }
                const transformationItem = _.merge(baseTransformationsItem, transformationsItem)

                variantsApi().updateComponentDataConsideringVariants(
                    compPointer,
                    transformationItem,
                    DATA_TYPES.transformations
                )
            }

            const writersToExecute = _.over([
                updateResponsiveLayout,
                updateStyle,
                updateInnerElements,
                updateTransformations
            ])
            const changes = conversionDal.getLastSnapshot()!.diff(initialSnapshot, PAGE_FRAGMENT_TYPE)
            const hasChanges =
                !_.isEmpty(changes[ConversionDalNamespaces.targetLayout]) ||
                !_.isEmpty(changes[ConversionDalNamespaces.style]) ||
                !_.isEmpty(changes[ConversionDalNamespaces.transformations])

            structureIterators.forEachComponent(
                pointer => {
                    const compPointerWithVariants = pointers.getPointer(pointer.id, VIEW_MODES.DESKTOP, {
                        variants: conversionAlgoConfig.targetVariants
                    })

                    writersToExecute(compPointerWithVariants, changes)
                },
                [
                    ChildrenProperties.components,
                    ChildrenProperties.hiddenComponents,
                    ChildrenProperties.backgroundComponents
                ]
            )

            updateLayoutSettings(conversionAlgoConfig, ctx)

            return {changeApplied: hasChanges}
        }

        const getLastGroupingHash = (compRef: CompRef): string | null => {
            return layoutSettingsApi().autoResponsivenessSettings.lastGroupingsHash.get(compRef) ?? null
        }

        const hasOnlyOneBreakpointVariant = (compPointer: CompRef): boolean => {
            if (compPointer.variants && compPointer.variants.length > 1) {
                return false
            }
            const variantData = dal.get(compPointer.variants![0])
            return variantData?.type === VARIANTS.VARIANT_TYPES.BREAKPOINT_RANGE
        }

        const withMobileVariant = <T>(action: (compPointer: CompRef) => T) => {
            return (compPointer: CompRef) => {
                let compPointerWithVariants = compPointer
                if (!compPointer.variants?.length) {
                    const targetVariants = [getTargetBreakpointForComponent(dal, pointers, compPointer)]
                    compPointerWithVariants = getCompPointerWithVariants(compPointer, targetVariants)
                } else if (!hasOnlyOneBreakpointVariant(compPointer)) {
                    throw new ReportableError({
                        message: 'CompRef has multiple variant pointers or variant is not a breakpoint range',
                        errorType: 'InvalidVariants',
                        extras: {
                            compPointer
                        }
                    })
                }
                return action(compPointerWithVariants)
            }
        }

        const isComponentLocked = withMobileVariant(
            (compRef: CompRef): boolean =>
                layoutSettingsApi().autoResponsivenessSettings.mode.get(compRef) ===
                AutoResponsivenessModeValue.Adaptive
        )

        const getMode = (compRef: CompRef): AutoResponsivenessMode =>
            isComponentLocked(compRef) ? AutoResponsivenessModeValue.Adaptive : AutoResponsivenessModeValue.Flush

        const getExecutionSettings = (
            compPointer: CompRef
        ): {
            mode: AutoResponsivenessMode
        } => {
            const mode = getMode(compPointer)
            return {
                mode
            }
        }

        const createConversionConfig = (
            containerPointer: CompRef,
            configurationOverrides?: ConversionAlgoConfigOverrides
        ): ConversionAlgoConfig => {
            const defaultTargetBreakpoint = getTargetBreakpointForComponent(dal, pointers, containerPointer)
            const rootCompRefInTargetContext = pointers.components.getComponentVariantsPointer(
                containerPointer,
                configurationOverrides?.targetVariants ?? [defaultTargetBreakpoint]
            ) as CompRef

            return _.defaults(configurationOverrides, {
                sourceVariants: containerPointer.variants,
                sourceCanvasSize: DOCUMENT_STAGE_WIDTH,
                targetVariants: [defaultTargetBreakpoint],
                targetCanvasSize: 390,
                ...getExecutionSettings(rootCompRefInTargetContext),
                lastGroupingHash: getLastGroupingHash(containerPointer),
                rootPointer: containerPointer,
                debug: false
            })
        }

        const runWithContext = async (
            containerPointer: CompRef,
            ctx: MobileAlgoContext,
            conversionAlgoConfig: ConversionAlgoConfig,
            algorithmRunId?: string
        ) => {
            let conversionData

            try {
                conversionData = prepareConversionData(containerPointer, conversionAlgoConfig, algorithmRunId)
            } catch (e: any) {
                if (!(e instanceof ReportableError)) {
                    throw getReportableFromError(e, {
                        errorType: 'PrepareConversionDataFailure'
                    })
                }

                throw e
            }

            const conversionDal = createConversionDal(conversionData.initialStore)
            const initialSnapshot = conversionDal.getLastSnapshot()!
            const structureIterators = createStructureIterators(conversionDal, getStructurePointer(containerPointer))
            const pageId = getPageId(containerPointer)
            const stageRunner = new StageRunner(stages, readOnlyExtensionAPI, conversionDal, conversionAlgoConfig)

            await stageRunner.run(
                ctx,
                conversionDal,
                initialSnapshot,
                pageId,
                conversionAlgoConfig,
                conversionAlgoConfig.debug ? actionsLogger : undefined
            )

            return {
                conversionDal,
                structureIterators,
                initialSnapshot
            }
        }

        const _run = async (
            compPointer: CompRef,
            configurationOverrides?: ConversionAlgoConfigOverrides,
            executionOptions?: MobileAlgoExecutionOptions,
            algorithmRunId?: string
        ) => {
            if (!isEnabled()) {
                const {components} = extensionAPI as StructureExtensionAPI
                return {
                    changeApplied: false,
                    rootComponentId: compPointer.id,
                    componentType: components.getComponentType(compPointer)
                }
            }

            const runId = algorithmRunId ?? _.uniqueId(RESPONSIFY_ALGO_RUN_ID_PREFIX)
            const conditionType = executionOptions?.conditionType ?? MobileAlgoConditionType.Unknown
            const conversionAlgoConfig: ConversionAlgoConfig = createConversionConfig(
                compPointer,
                configurationOverrides
            )
            const compPointerWithTargetVariants = getCompPointerWithVariants(
                compPointer,
                conversionAlgoConfig.targetVariants
            )

            const {components} = extensionAPI as StructureExtensionAPI
            const componentType = components.getComponentType(compPointer)

            if (layoutSettingsApi().autoResponsivenessSettings.isSynced.get(compPointerWithTargetVariants)) {
                return {changeApplied: false, rootComponentId: compPointer.id, componentType}
            }

            logger.interactionStarted(RESPONSIFY_ALGO_RUN_INTERACTION_NAME, {
                extras: {
                    algorithmRunId: runId,
                    conditionType,
                    rootComponentId: compPointer.id,
                    rootComponentType: componentType
                }
            })

            const ctx = createContext(conversionAlgoConfig.plugins as any)
            const {conversionDal, initialSnapshot, structureIterators} = await runWithContext(
                compPointer,
                ctx,
                conversionAlgoConfig,
                runId
            )

            const {changeApplied} = writeConversionResult(
                conversionDal,
                initialSnapshot!,
                structureIterators,
                conversionAlgoConfig,
                ctx
            )
            dal.commitTransaction(RESPONSIFY_ALGO_TRANSACTION_NAME)

            logger.interactionEnded(RESPONSIFY_ALGO_RUN_INTERACTION_NAME, {
                extras: {
                    algorithmRunId: runId,
                    conditionType,
                    rootComponentId: compPointer.id,
                    rootComponentType: componentType,
                    changeApplied
                }
            })

            return {changeApplied, rootComponentId: compPointer.id, componentType}
        }

        const withMobileAlgoExecutionLock = async <T>(operation: () => Promise<T>): Promise<T | void> => {
            if (getMobileAlgoExecutionState()) {
                return logger.captureError(
                    new ReportableError({
                        message: 'duplicate call to responsify algorithm',
                        errorType: 'ResponsifyAlgoOperationInProgress'
                    })
                )
            }

            dal.commitTransaction(RESPONSIFY_ALGO_BASELINE_TRANSACTION_NAME)
            setMobileAlgoExecutionState(true)
            try {
                return await operation()
            } finally {
                setMobileAlgoExecutionState(false)
            }
        }

        const run = async (
            compPointer: CompRef,
            configurationOverrides?: ConversionAlgoConfigOverrides,
            executionOptions?: MobileAlgoExecutionOptions
        ) => {
            const runId = _.uniqueId(RESPONSIFY_ALGO_RUN_ID_PREFIX)
            await withMobileAlgoExecutionLock(() => _run(compPointer, configurationOverrides, executionOptions, runId))
        }

        const getComponentsToRun = (pagePointer: CompRef, includeSharedParts = true): CompRef[] => {
            const pageChildren = pointers.structure.getChildren(pagePointer)
            const {components} = extensionAPI as StructureExtensionAPI

            return _.uniqBy(
                pageChildren.reduce((compRefs: Pointer[], child: Pointer) => {
                    const compType = components.getComponentType(child)

                    if (compType === COMP_TYPES.RESPONSIVE_SECTION) {
                        compRefs.push(child)
                    }

                    const referredComp =
                        includeSharedParts && pointers.structure.getSharedBlockFromInstance(child as CompRef)

                    if (referredComp && SECTION_COMP_TYPES.has(components.getComponentType(referredComp))) {
                        compRefs.push(referredComp)
                    } else if ((extensionAPI as LayoutExtensionApi).components.isPinned(child)) {
                        compRefs.push(child)
                    }

                    return compRefs
                }, []) as CompRef[],
                'id'
            )
        }

        const getSectionsFromPages = (pagesPointers: CompRef[]) => {
            return _.uniqBy(pagesPointers.map(pagePointer => getComponentsToRun(pagePointer)).flat(), 'id')
        }

        const _runForPagesInternal = async (
            pagesPointers: CompRef[],
            configOverrides?: ConversionAlgoConfigOverrides,
            executionOptions?: MobileAlgoExecutionOptions,
            loggingContext?: {scope: string; pageId?: string; pageIds?: string[]}
        ) => {
            const runId = _.uniqueId(RESPONSIFY_ALGO_BATCH_RUN_ID_PREFIX)
            const componentsToRun = getSectionsFromPages(pagesPointers)
            const conditionType = executionOptions?.conditionType ?? MobileAlgoConditionType.Unknown

            logger.interactionStarted(RESPONSIFY_ALGO_BATCH_INTERACTION_NAME, {
                extras: {
                    algorithmRunId: runId,
                    conditionType,
                    rootComponentsCount: componentsToRun.length,
                    ...loggingContext
                }
            })

            const results = await Promise.all(
                componentsToRun.map(pointer => _run(pointer, configOverrides, executionOptions, runId))
            )

            const affectedRootComponents = results
                .filter(r => r?.changeApplied)
                .map(r => ({id: r.rootComponentId, componentType: r.componentType}))

            logger.interactionEnded(RESPONSIFY_ALGO_BATCH_INTERACTION_NAME, {
                extras: {
                    algorithmRunId: runId,
                    conditionType,
                    rootComponentsCount: componentsToRun.length,
                    affectedRootComponents,
                    changeApplied: affectedRootComponents.length > 0,
                    ...loggingContext
                }
            })
        }

        const runForPages = async (
            pagesIds: string[],
            configOverrides?: ConversionAlgoConfigOverrides,
            executionOptions?: MobileAlgoExecutionOptions,
            loggingContext?: {scope: string; pageId?: string; pagesIds?: string[]}
        ): Promise<void> => {
            await withMobileAlgoExecutionLock(async () => {
                const pagePointers = pagesIds.map(pageId => pointers.structure.getPage(pageId, VIEW_MODES.DESKTOP))
                await _runForPagesInternal(
                    pagePointers,
                    configOverrides,
                    executionOptions,
                    loggingContext ?? {
                        scope: 'pages',
                        pagesIds
                    }
                )
            })
        }

        const runForPage = async (
            pagePointer: CompRef,
            configOverrides?: ConversionAlgoConfigOverrides,
            executionOptions?: MobileAlgoExecutionOptions
        ): Promise<void> => {
            await runForPages([pagePointer.id], configOverrides, executionOptions, {
                scope: 'page',
                pageId: pagePointer.id
            })
        }

        const runForAllPages = async (executionOptions?: MobileAlgoExecutionOptions): Promise<void> => {
            const allPagesIds = pages().getAllPagesIds(false)
            await runForPages(allPagesIds, undefined, executionOptions, {scope: 'allPages'})
        }

        const getSectionsFromAllPages = () => {
            const allPagesPointers = pages()
                .getAllPagesIds(false)
                .map(pageId => pointers.structure.getPage(pageId, VIEW_MODES.DESKTOP))
            return getSectionsFromPages(allPagesPointers)
        }

        const recalculateHash = async (compPointer: CompRef, algorithmRunId?: string) => {
            /**
             * We need to optimize it so we won't run all calculations after we calculated the hash
             * It's not significant because currently the 'slowest' part is the network call, which is required
             */
            const runId = algorithmRunId ?? _.uniqueId(RESPONSIFY_ALGO_RUN_ID_PREFIX)
            const conversionAlgoConfig: ConversionAlgoConfig = createConversionConfig(compPointer)
            const ctx = createContext()
            await runWithContext(compPointer, ctx, conversionAlgoConfig, runId)

            updateLayoutSettings(conversionAlgoConfig, ctx)
        }

        const softUnlockSection = (compPointer: CompRef) => {
            const targetVariants = [getTargetBreakpointForComponent(dal, pointers, compPointer)]
            const compPointerWithTargetVariants = getCompPointerWithVariants(compPointer, targetVariants)
            layoutSettingsApi().autoResponsivenessSettings.lastGroupingsHash.remove(compPointer)
            layoutSettingsApi().autoResponsivenessSettings.isSynced.remove(compPointerWithTargetVariants)
        }

        const lockingActions = {
            [LockingAction.Lock]: withMobileVariant((sectionPointer: CompRef) =>
                layoutSettingsApi().autoResponsivenessSettings.mode.set(
                    sectionPointer,
                    AutoResponsivenessModeValue.Adaptive
                )
            ),
            [LockingAction.Unlock]: withMobileVariant((sectionPointer: CompRef) =>
                layoutSettingsApi().autoResponsivenessSettings.mode.remove(sectionPointer)
            ),
            [LockingAction.SoftLock]: recalculateHash,
            [LockingAction.SoftUnlock]: softUnlockSection,
            [LockingAction.RemoveLastGroupingHash]: (sectionPointer: CompRef) =>
                layoutSettingsApi().autoResponsivenessSettings.lastGroupingsHash.remove(sectionPointer),
            [LockingAction.RemoveExecutionLocks]: (compPointer: CompRef) => {
                lockingActions[LockingAction.Unlock](compPointer)
                lockingActions[LockingAction.SoftUnlock](compPointer)
            }
        }

        const getExecuteActionForPageAsync = (action: LockingAction) => {
            return (pagePointer: CompRef) => {
                const sections = getComponentsToRun(pagePointer, false)
                return Promise.all(sections.map((compRef: CompRef) => lockingActions[action](compRef)))
            }
        }

        const getExecuteActionForAllPagesAsync = (action: LockingAction) => {
            return async () => {
                const componentsToRunFromAllPages = getSectionsFromAllPages()

                return Promise.all(
                    componentsToRunFromAllPages.map((compRef: CompRef) => lockingActions[action](compRef))
                )
            }
        }

        const getExecuteActionForPage = (action: LockingAction) => {
            return (pagePointer: CompRef) => {
                const sections = getComponentsToRun(pagePointer, false)
                sections.forEach((compRef: CompRef) => lockingActions[action](compRef))
            }
        }

        const getExecuteActionForAllPages = (action: LockingAction) => {
            return () => {
                const componentsToRunFromAllPages = getSectionsFromAllPages()
                componentsToRunFromAllPages.forEach((compRef: CompRef) => lockingActions[action](compRef))
            }
        }

        return {
            mobileAlgo: {
                locking: {
                    isComponentLocked,
                    unlockSectionsForAllPages: getExecuteActionForAllPages(LockingAction.Unlock),
                    unlockSectionsForPage: getExecuteActionForPage(LockingAction.Unlock),
                    unlockSection: lockingActions[LockingAction.Unlock],
                    lockSection: lockingActions[LockingAction.Lock],
                    lockSectionsForPage: getExecuteActionForPage(LockingAction.Lock),
                    lockSectionsForAllPages: getExecuteActionForAllPages(LockingAction.Lock),
                    softLock: lockingActions[LockingAction.SoftLock],
                    softLockPage: getExecuteActionForPageAsync(LockingAction.SoftLock),
                    softLockAllPages: getExecuteActionForAllPagesAsync(LockingAction.SoftLock),
                    softUnlock: lockingActions[LockingAction.SoftUnlock],
                    softUnlockSectionsForPage: getExecuteActionForPage(LockingAction.SoftUnlock),
                    softUnlockSectionsForAllPages: getExecuteActionForAllPages(LockingAction.SoftUnlock),
                    removeExecutionLocks: lockingActions[LockingAction.RemoveExecutionLocks],
                    removeExecutionLocksForPage: getExecuteActionForPage(LockingAction.RemoveExecutionLocks),
                    removeExecutionLocksForAllPages: getExecuteActionForAllPages(LockingAction.RemoveExecutionLocks)
                },
                plugins: {
                    register: registerPlugin,
                    getApis: getPluginApis
                },
                context: {
                    create: createContext,
                    enable: enablePluginsForContext,
                    disable: disablePluginsForContext
                },
                heuristics: {
                    registerHeuristic,
                    registerInnerElementHeuristic,
                    registerPostStageOperation
                },
                groupings: {
                    getLastGroupingHash,
                    removeLastGroupingHashFromAllPages: getExecuteActionForAllPages(
                        LockingAction.RemoveLastGroupingHash
                    ),
                    removeLastGroupingHashFromPage: getExecuteActionForPage(LockingAction.RemoveLastGroupingHash),
                    removeLastGroupingHashFromSection: lockingActions[LockingAction.RemoveLastGroupingHash]
                },
                utils: {
                    getFontSize
                },
                settings: {
                    enable,
                    disable,
                    isEnabled
                },
                createConversionConfig,
                runWithContext,
                writeConversionResult,
                run,
                runForAllPages,
                runForPage,
                runForPages,
                state: {
                    isRunning: getMobileAlgoExecutionState
                }
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis) => {
        const {mobileAlgo} = extensionAPI as MobileAlgoExtensionApi

        const wrapAsyncFunction = (fn: MobileAlgoRunFunction) => {
            return async (
                containerPointer: CompRef,
                resolvedFunctions?: ResolvedFunctions,
                config?: ConversionAlgoConfigOverrides,
                executionOptions?: MobileAlgoExecutionOptions
            ) => {
                const conditionType = executionOptions?.conditionType ?? MobileAlgoConditionType.Manual
                try {
                    await fn(containerPointer, config, {conditionType})
                    resolvedFunctions?.onSuccess?.()
                } catch (e: any) {
                    logger.captureError(e)
                    resolvedFunctions?.onError?.()
                }
            }
        }

        return {
            mobileAlgo: {
                run: wrapAsyncFunction(mobileAlgo.run),
                runForPage: wrapAsyncFunction(mobileAlgo.runForPage),
                locking: {
                    isComponentLocked: mobileAlgo.locking.isComponentLocked,
                    unlockSectionsForAllPages: mobileAlgo.locking.unlockSectionsForAllPages,
                    unlockSectionsForPage: mobileAlgo.locking.unlockSectionsForPage,
                    unlockSection: mobileAlgo.locking.unlockSection,
                    lockSection: mobileAlgo.locking.lockSection,
                    lockSectionsForPage: mobileAlgo.locking.lockSectionsForPage,
                    lockSectionsForAllPages: mobileAlgo.locking.lockSectionsForAllPages,
                    softLock: mobileAlgo.locking.softLock,
                    softLockPage: mobileAlgo.locking.softLockPage,
                    softLockAllPages: mobileAlgo.locking.softLockAllPages,
                    softUnlock: mobileAlgo.locking.softUnlock,
                    softUnlockSectionsForPage: mobileAlgo.locking.softUnlockSectionsForPage,
                    softUnlockSectionsForAllPages: mobileAlgo.locking.softUnlockSectionsForAllPages,
                    removeExecutionLocks: mobileAlgo.locking.removeExecutionLocks,
                    removeExecutionLocksForPage: mobileAlgo.locking.removeExecutionLocksForPage,
                    removeExecutionLocksForAllPages: mobileAlgo.locking.removeExecutionLocksForAllPages
                },
                groupings: {
                    removeLastGroupingHashFromAllPages: mobileAlgo.groupings.removeLastGroupingHashFromAllPages,
                    removeLastGroupingHashFromPage: mobileAlgo.groupings.removeLastGroupingHashFromPage,
                    removeLastGroupingHashFromSection: mobileAlgo.groupings.removeLastGroupingHashFromSection
                },
                settings: {
                    enable: mobileAlgo.settings.enable,
                    disable: mobileAlgo.settings.disable,
                    isEnabled: mobileAlgo.settings.isEnabled
                },
                utils: {
                    getFontSize: mobileAlgo.utils.getFontSize
                }
            }
        }
    }

    const createTransactionDerivedMetadata = ({
        dal,
        extensionAPI,
        coreConfig,
        pointers
    }: CreateExtArgs): Record<string, [PostTransactionOperation, string?]> => {
        const layoutSettingsApi = () => (extensionAPI as LayoutSettingsExtensionAPI).layoutSettings
        const relationshipsAPI = (): RelationshipsAPI['relationships'] =>
            (extensionAPI as RelationshipsAPI).relationships
        const mobileAlgoAPI = (): MobileAlgoApi => (extensionAPI as MobileAlgoExtensionApi).mobileAlgo
        const RELEVANT_NAMESPACES = new Set([DATA_TYPES.layout, DATA_TYPES.editorsettings, VIEW_MODES.DESKTOP])

        const isRootPinnedComponent = (compPointer: CompRef) => {
            const {components} = extensionAPI as LayoutExtensionApi
            const parentPointer = pointers.structure.getParent(compPointer)
            return parentPointer && components.isPinned(compPointer) && pointers.structure.isPage(parentPointer)
        }

        const getRootSectionOrPinned = (compPointer: CompRef): CompRef | null => {
            const {components} = extensionAPI as StructureExtensionAPI
            let current: CompRef | null = compPointer

            while (current) {
                const componentType = components.getComponentType(current)

                if (SECTION_COMP_TYPES.has(componentType) || isRootPinnedComponent(current)) {
                    return current
                }

                current = pointers.structure.getParent(current) as CompRef
            }

            return null
        }

        return {
            removeMobileAlgoExecutionMark: [
                (documentTransaction: Transaction) => {
                    if (mobileAlgoAPI().state.isRunning()) {
                        return
                    }
                    const changedComponents = new Set<string>()
                    const structuralChange = new Map<string, boolean>()

                    coreConfig.logger.interactionStarted('removeMobileAlgoExecutionMark', {
                        extras: {
                            transactionId: documentTransaction.id,
                            itemsCount: documentTransaction.items.length
                        }
                    })

                    for (const item of documentTransaction.items) {
                        const pointer = item?.key as Pointer

                        if (!pointer || !RELEVANT_NAMESPACES.has(pointer.type)) {
                            continue
                        }

                        const [componentPointer] = relationshipsAPI().getComponentsEventuallyReferencingItem(pointer)

                        if (!componentPointer) {
                            continue
                        }

                        const rootComp = getRootSectionOrPinned(componentPointer as CompRef)
                        const rootCompId = rootComp?.id

                        if (!rootCompId) {
                            continue
                        }
                        if (pointer.type === VIEW_MODES.DESKTOP) {
                            structuralChange.set(rootCompId, true)
                        }
                        changedComponents.add(rootCompId)
                    }

                    for (const componentId of changedComponents) {
                        const compPointer = pointerUtils.getPointer(componentId, VIEW_MODES.DESKTOP) as CompRef
                        const targetVariants = [getTargetBreakpointForComponent(dal, pointers, compPointer)]

                        const compRefInTargetContext = targetVariants
                            ? pointers.components.getComponentVariantsPointer(compPointer, targetVariants)
                            : compPointer
                        layoutSettingsApi().autoResponsivenessSettings.isSynced.remove(compRefInTargetContext)

                        if (structuralChange.get(componentId)) {
                            layoutSettingsApi().autoResponsivenessSettings.lastGroupingsHash.remove(compPointer)
                        }
                    }

                    coreConfig.logger.interactionEnded('removeMobileAlgoExecutionMark', {
                        extras: {
                            transactionId: documentTransaction.id,
                            rootComponentsUpdated: changedComponents.size
                        }
                    })
                },
                PAGE_FRAGMENT_TYPE
            ]
        }
    }

    const createValidator = ({extensionAPI, pointers}: DmApis): ValidatorMap => {
        const isExcludedComponent = (compPointer: Pointer) => {
            if (pointers.structure.isPage(compPointer)) {
                return true
            }
            const parent = pointers.structure.getParent(compPointer)
            if (!parent) {
                return true
            }
            if (pointers.structure.isPage(parent)) {
                return true
            }
            const {components} = extensionAPI as StructureExtensionAPI & LayoutExtensionApi
            const parentType = components.getComponentType(parent)
            return SECTION_COMP_TYPES.has(parentType)
        }

        const validateSingleLayoutData = (pointer: Pointer, value: DalValue) => {
            if (!value || pointer.type !== DATA_TYPES.layout || value.type !== 'SingleLayoutData') {
                return
            }

            const {rendererModel} = extensionAPI as RMApi

            if (!rendererModel.isBuilderComponentModel()) {
                return
            }

            if ('hidden' in (value.componentLayout ?? {})) {
                return
            }

            if (
                _.isEmpty(value.itemLayout) ||
                (value.itemLayout.type === 'GridItemLayout' && _.isEmpty(value.itemLayout.gridArea))
            ) {
                const {relationships} = extensionAPI as RelationshipsAPI
                const {slots} = extensionAPI as SlotsExtensionAPI
                const [compPointer] = relationships.getComponentsEventuallyReferencingItem(pointer)

                if (!compPointer || isExcludedComponent(compPointer) || slots.isChildOfSlottedComp(compPointer.id)) {
                    return
                }

                return [
                    {
                        shouldFail: experimentInstance.isOpen('dm_failOnCorruptSingleLayoutData'),
                        type: 'corruptedSingleLayoutData',
                        message: 'Missing itemLayout properties',
                        extras: {
                            compId: compPointer.id,
                            layout: value
                        }
                    }
                ]
            }
        }

        return {
            validateSingleLayoutData
        }
    }

    const extension: Extension = {
        name: 'mobileAlgo',
        initialize: async ({extensionAPI}: DmApis) => {
            const mobileAlgoApi = extensionAPI as MobileAlgoExtensionApi
            bootPlugins.forEach(pluginFactory => {
                mobileAlgoApi.mobileAlgo.plugins.register(pluginFactory)
            })
        },
        createExtensionAPI,
        createValidator,
        createTransactionDerivedMetadata,
        createPublicAPI
    }

    return extension
}

export {createExtension}
